import { useEffect, useRef, useState } from 'react';
import { PiEmpty, PiError, PiLoading, piCount } from '@/components/product-insights/pi-kit';
import { Button, CollapsibleSection, type CollapsibleSectionProps, ConfirmDialog } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { ApiError, api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import type { PlatformCompanyLinkSummary } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { LinkPlatformCompanyDialog, type LinkPlatformCompanyTarget } from './LinkPlatformCompanyDialog';
import { EntityTypeBadge } from './EntityTypeBadge';
import { mapperErrorMessage } from './mapper-ui';

/**
 * The company page's "Company mapping" section (owner 2026-10-25, M6; layout
 * section id `mapping`): the SEED platform company records linked to THIS
 * company (many records → one company, M2), with link and unlink.
 *
 * - Renders NOTHING for a reader without `product-insights.read` (no fetch),
 *   and nothing when the server refuses the read (403). Link / unlink need
 *   `product-insights.update` (M8).
 * - "Link SEED platform record" opens `LinkPlatformCompanyDialog` over the
 *   directory; its unticked "Also set Company name to …" (M5) is offered only
 *   with `companies.update`. When a rename lands, `onCompanyRenamed` gets the
 *   new Company name so the host can refresh its header in place.
 * - With `shell` the section draws its own `CollapsibleSection` (title
 *   "Company mapping", the link count as its summary, the link button as its
 *   action); without it, only the body.
 */

export interface CompanyMappingSectionProps {
  companyId: number;
  companyName: string;
  onCompanyRenamed?: (name: string) => void;
  shell?: Omit<CollapsibleSectionProps, 'title' | 'children' | 'summary' | 'actions'>;
}

type Load =
  | { status: 'loading' }
  | { status: 'denied' }
  | { status: 'error'; message: string }
  | { status: 'ready'; links: PlatformCompanyLinkSummary[] };

export function CompanyMappingSection(props: CompanyMappingSectionProps) {
  const { ability } = useAuth();
  if (!ability.canRead('product-insights')) return null;
  return <SectionContent {...props} />;
}

function SectionContent({ companyId, companyName, onCompanyRenamed, shell }: CompanyMappingSectionProps) {
  const { ability } = useAuth();
  const canEdit = ability.canUpdate('product-insights');

  const [reloadToken, setReloadToken] = useState(0);
  const requestKey = `${companyId}#${reloadToken}`;
  const [fetched, setFetched] = useState<{ key: string; load: Load } | null>(null);
  const load: Load = fetched !== null && fetched.key === requestKey ? fetched.load : { status: 'loading' };

  const [linkTarget, setLinkTarget] = useState<LinkPlatformCompanyTarget | null>(null);
  const [unlinkTarget, setUnlinkTarget] = useState<PlatformCompanyLinkSummary | null>(null);
  const [unlinkBusy, setUnlinkBusy] = useState(false);
  const [unlinkError, setUnlinkError] = useState<string | null>(null);
  const [renamed, setRenamed] = useState<string | null>(null);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    api
      .listCompanyPlatformLinks(companyId, signal)
      .then((links) => {
        if (!signal.aborted) setFetched({ key: requestKey, load: { status: 'ready', links } });
      })
      .catch((error: unknown) => {
        if (signal.aborted) return;
        if (error instanceof ApiError && error.status === 403) {
          setFetched({ key: requestKey, load: { status: 'denied' } });
          return;
        }
        setFetched({
          key: requestKey,
          load: { status: 'error', message: mapperErrorMessage(error, 'Linked SEED platform records could not be loaded.') },
        });
      });
    return () => controller.abort();
  }, [companyId, requestKey]);

  if (load.status === 'denied') return null;

  const reload = () => setReloadToken((n) => n + 1);

  const confirmUnlink = () => {
    const link = unlinkTarget;
    if (link === null) return;
    setUnlinkBusy(true);
    setUnlinkError(null);
    api
      .deletePlatformCompanyLink(link.platformCompanyId)
      .then(() => {
        if (!mounted.current) return;
        setFetched((current) =>
          current !== null && current.load.status === 'ready'
            ? {
                key: current.key,
                load: {
                  status: 'ready',
                  links: current.load.links.filter((entry) => entry.platformCompanyId !== link.platformCompanyId),
                },
              }
            : current,
        );
        setUnlinkTarget(null);
      })
      .catch((error: unknown) => {
        if (mounted.current) setUnlinkError(mapperErrorMessage(error, 'The link could not be removed.'));
      })
      .finally(() => {
        if (mounted.current) setUnlinkBusy(false);
      });
  };

  const linkButton = canEdit ? (
    <Button
      size="sm"
      variant="secondary"
      className="max-sm:h-11"
      onClick={() => setLinkTarget({ mode: 'choose-platform', company: { id: companyId, name: companyName } })}
    >
      Link SEED platform record
    </Button>
  ) : null;

  let content;
  if (load.status === 'loading') {
    content = <PiLoading variant="table" rows={2} />;
  } else if (load.status === 'error') {
    content = <PiError message={load.message} onRetry={reload} />;
  } else if (load.links.length === 0) {
    content = <PiEmpty title="No SEED platform records linked" action={shell === undefined ? linkButton : undefined} />;
  } else {
    content = (
      <ul className="m-0 list-none divide-y divide-hairline p-0">
        {load.links.map((link) => {
          const byline = [link.linkedBy?.name, link.linkedAt ? formatDateTime(link.linkedAt) : null]
            .filter((part): part is string => typeof part === 'string' && part !== '')
            .join(' · ');
          return (
            <li
              key={link.platformCompanyId}
              className="flex min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-2 py-2 first:pt-0 last:pb-0"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="font-medium break-words text-fg">{link.platformCompanyName}</span>
                  <EntityTypeBadge entityType={link.entityType} />
                </div>
                {byline !== '' && <div className="text-xs text-fg-muted">Linked by {byline}</div>}
              </div>
              {canEdit && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="max-sm:h-11"
                  aria-label={`Unlink ${link.platformCompanyName}`}
                  onClick={() => {
                    setUnlinkError(null);
                    setUnlinkTarget(link);
                  }}
                >
                  Unlink
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  const body = (
    <div className="grid min-w-0 gap-3 px-4 py-3">
      {shell === undefined && load.status === 'ready' && load.links.length > 0 && linkButton !== null && (
        <div className="flex justify-end">{linkButton}</div>
      )}
      {renamed !== null && (
        <Notice
          tone="success"
          role="status"
          action={
            <Button size="sm" variant="ghost" onClick={() => setRenamed(null)}>
              Dismiss
            </Button>
          }
        >
          {`Company name set to ${renamed}.`}
        </Notice>
      )}
      {content}

      <LinkPlatformCompanyDialog
        target={linkTarget}
        onClose={() => setLinkTarget(null)}
        onLinked={({ adoptedName }) => {
          if (adoptedName !== null) {
            setRenamed(adoptedName);
            onCompanyRenamed?.(adoptedName);
          }
          reload();
        }}
      />

      <ConfirmDialog
        open={unlinkTarget !== null}
        onClose={() => {
          if (!unlinkBusy) setUnlinkTarget(null);
        }}
        onConfirm={confirmUnlink}
        title="Unlink SEED platform record"
        description={
          unlinkTarget !== null ? (
            <>
              Unlink <strong>{unlinkTarget.platformCompanyName}</strong> from <strong>{companyName}</strong>? Its SEED
              platform figures stop showing on this company.
            </>
          ) : null
        }
        confirmLabel="Unlink"
        tone="danger"
        loading={unlinkBusy}
        error={unlinkError}
      />
    </div>
  );

  if (shell === undefined) return body;

  return (
    <CollapsibleSection
      {...shell}
      title="Company mapping"
      summary={load.status === 'ready' ? `${piCount(load.links.length)} linked` : undefined}
      actions={linkButton ?? undefined}
    >
      {body}
    </CollapsibleSection>
  );
}
